import type { DropoffAddress, HeldQuote } from '../types'
import { deliveryErrorKindFromStatus, type DeliveryErrorKind } from './deliveryErrorUx'

export type DeliveryQuoteResult =
  | { kind: 'quoted'; quote: HeldQuote }
  | { kind: 'out_of_range' }
  | { kind: 'error'; errorKind: DeliveryErrorKind }

/**
 * Pide una cotización a /api/delivery/quote y la traduce a un resultado cerrado.
 *
 * Extraído de `AddressStep.quote()` para que la re-cotización silenciosa del
 * checkout (spec 2026-09-03 §4.10) use EXACTAMENTE el mismo parseo. El `quote`
 * devuelto es VERBATIM del servidor: `feeAmountCents` es la única verdad del
 * envío y aquí no se toca. `fetchFn` es inyectable solo para tests.
 */
export async function requestDeliveryQuote(
  locationId: string,
  dropoff: Pick<DropoffAddress, 'street' | 'city' | 'state' | 'zip' | 'lat' | 'lng' | 'unit'>,
  fetchFn: typeof fetch = fetch,
): Promise<DeliveryQuoteResult> {
  let res: Response
  try {
    res = await fetchFn('/api/delivery/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locationId,
        dropoff: {
          street: dropoff.street,
          city: dropoff.city,
          state: dropoff.state,
          zip: dropoff.zip,
          ...(dropoff.lat != null && dropoff.lng != null ? { lat: dropoff.lat, lng: dropoff.lng } : {}),
          // Apto (H-2): no cambia el precio; se congela con el quote para el courier.
          ...(dropoff.unit?.trim() ? { unit: dropoff.unit.trim() } : {}),
        },
      }),
    })
  } catch {
    // El fetch ni salió: se cayó la red del cliente. Transitorio de verdad.
    return { kind: 'error', errorKind: 'network' }
  }

  if (!res.ok) return { kind: 'error', errorKind: deliveryErrorKindFromStatus(res.status) }

  const data = (await res.json().catch(() => null)) as
    | { serviceable?: boolean; quote?: HeldQuote & { currency?: string } }
    | null

  if (!data) return { kind: 'error', errorKind: 'quote_failed' }
  if (data.serviceable === false) return { kind: 'out_of_range' }
  if (!data.serviceable || !data.quote?.quoteId) return { kind: 'error', errorKind: 'quote_failed' }

  return {
    kind: 'quoted',
    quote: {
      quoteId: data.quote.quoteId,
      // VERBATIM del servidor. Único lugar donde el fee entra al checkout.
      feeAmountCents: data.quote.feeAmountCents,
      expiresAt: data.quote.expiresAt,
      ...(data.quote.etaDropoffSeconds != null ? { etaDropoffSeconds: data.quote.etaDropoffSeconds } : {}),
    },
  }
}
